import {
    select as D3select,
    geoPath as D3geoPath,
    scaleSqrt as D3scaleSqrt,
    symbol as D3symbol,
    symbolTriangle as D3symbolTriangle,
    easeLinear as D3easeLinear,
    dispatch as D3dispatch,
} from 'd3';

/*
    expects: {key, path, direction}
*/

const D3AnimatedPath = function () {
    let color;
    let projection;

    const dispatch = D3dispatch('mouseover', 'mouseout');
    let self;
    let scale = 1;
    let theme;

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            self = this;
            const dpath = D3geoPath().projection(projection);
            const triScale = D3scaleSqrt()
                .domain([1, 42])
                .range([50 / scale, 1 / scale]);
            const triangleSize = triScale(scale);

            let g = D3select(self).selectAll('.brcanimatedpath').data([data], d => d.key);
            g = g.enter()
                .append('g').attr('class', 'brcanimatedpath')
                .merge(g);

            const pjoin = g.selectAll('path.animated').data([data], d => d.key);
            pjoin.exit().remove();
            const penter = pjoin.enter()
                .append('path')
                .attr('class', 'animated')
                .attr('fill', 'none')
                .attr('d', d => dpath({ type: 'LineString', coordinates: d.path }))
                .attr('stroke', color)
                .style('stroke-linecap', 'round')
                .style('stroke-dasharray', function (d) { return `0, ${this.getTotalLength()}` });

            // grow the path
            penter.transition()
                .duration(theme.transitions.duration.missles / scale)
                .ease(D3easeLinear)
                .style('stroke-dasharray', function (d) { return `${this.getTotalLength()},0` });

            penter.merge(pjoin)
                .attr('stroke-width', 2 / scale)
                .attr('d', d => dpath({ type: 'LineString', coordinates: d.path }))
                .each(function (d) { d.node = this })
                .on('mouseover', function (e, d) {
                    return dispatch.call('mouseover', my, { ...{ node: D3select(this).node() }, ...d });
                })
                .on('mouseout', function (e, d) {
                    return dispatch.call('mouseout', my, { ...{ node: D3select(this).node() }, ...d });
                });

            // add the arrow at the end of the line
            const ajoin = g.selectAll('path.toparrow').data([data], d => d.key);
            ajoin.exit().remove();
            const aenter = ajoin.enter()
                .append('path')
                .attr('class', 'toparrow')
                .attr('d', D3symbol().size(triangleSize).type(D3symbolTriangle)())
                .style('fill', color);

            // move the arrow along the path as it grows
            aenter.transition()
                .duration(theme.transitions.duration.missles / scale)
                .ease(D3easeLinear)
                .attrTween('transform', (d, i) => translateAlong(d, i)())
                .remove();

            // add additional traveling arrows
            const gdots = g.selectAll('.arrow').data([data], d => d.key);
            gdots.exit().interrupt().remove();
            gdots.enter()
                .append('path')
                .attr('class', 'arrow')
                .style('fill', color)
                .style('opacity', 0)
                .merge(gdots)
                .attr('d', D3symbol().size(triangleSize).type(D3symbolTriangle)())
                .each(animate);

            // helper functions
            function animate (d, i) {
                D3select(this)
                    .transition()
                    .duration(theme.transitions.duration.missles * 2)
                    .on('start', function () { D3select(this).style('opacity', 1) })
                    .attrTween('transform', translateAlong(d, i))
                    .on('end', animate);
            }

            function translateAlong (d, i) {
                const node = d.node;
                if (!node) return;
                const l = node.getTotalLength();
                return (d, i, a) => {
                    return (t) => {
                        const p1 = node.getPointAtLength(t * l);
                        const p2 = node.getPointAtLength((t * l) + 1);
                        const a = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI) + 90;
                        return `translate(${p1.x},${p1.y})rotate(${a})`;
                    };
                };
            }
        });
    }

    // getters/setters
    my.color = function (v) {
        if (!arguments.length) return color;
        color = v;
        return my;
    };

    my.projection = function (v) {
        if (!arguments.length) return projection;
        projection = v;
        return my;
    };

    my.scale = function (v) {
        if (!arguments.length) return scale;
        scale = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3AnimatedPath;
